import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  BarChart3,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Percent,
  Activity,
  Loader2,
  Play,
  History,
  Target,
  AlertTriangle,
  Trophy,
  Skull,
  ArrowUpRight,
  ArrowDownRight,
  Info,
  ChevronDown,
  LineChart
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { LineChart as RechartsLineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Strategy configurations with icons and colors
const STRATEGIES = {
  rsi: {
    name: 'RSI',
    fullName: 'Relative Strength Index',
    color: 'text-purple-400',
    bgColor: 'bg-purple-500/20',
    icon: Activity
  },
  macd: {
    name: 'MACD',
    fullName: 'MACD Crossover',
    color: 'text-blue-400',
    bgColor: 'bg-blue-500/20',
    icon: TrendingUp
  },
  ma_cross: {
    name: 'MA Cross',
    fullName: 'Moving Average Cross',
    color: 'text-yellow-400',
    bgColor: 'bg-yellow-500/20',
    icon: LineChart
  },
  combined: {
    name: 'Combinée',
    fullName: 'Multi-Indicateurs',
    color: 'text-green-400',
    bgColor: 'bg-green-500/20',
    icon: Target
  }
};

// Period options
const PERIODS = [
  { value: '1mo', label: '1 Mois' },
  { value: '3mo', label: '3 Mois' },
  { value: '6mo', label: '6 Mois' },
  { value: '1y', label: '1 An' },
  { value: '2y', label: '2 Ans' }
];

// Timeframe options
const TIMEFRAMES = [
  { value: '1H', label: '1 Heure' },
  { value: '1D', label: '1 Jour' },
  { value: '1W', label: '1 Semaine' }
];

// Stat Card component
const StatCard = ({ icon: Icon, label, value, subValue, color = 'text-primary', bgColor = 'bg-primary/10' }) => (
  <div className="bg-card/50 rounded-lg p-4 border border-border/50">
    <div className="flex items-center gap-2 mb-2">
      <div className={`p-2 rounded-lg ${bgColor}`}>
        <Icon className={`w-4 h-4 ${color}`} />
      </div>
      <span className="text-sm text-muted-foreground">{label}</span>
    </div>
    <div className={`text-2xl font-bold ${color}`}>{value}</div>
    {subValue && <div className="text-xs text-muted-foreground mt-1">{subValue}</div>}
  </div>
);

// Trade row component
const TradeRow = ({ trade, index }) => {
  const isWin = trade.pnl > 0;
  
  return (
    <tr className="border-b border-border/30 hover:bg-muted/20 transition-colors">
      <td className="py-3 px-4 text-sm">{index + 1}</td>
      <td className="py-3 px-4">
        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${
          trade.direction === 'LONG' 
            ? 'bg-green-500/20 text-green-400' 
            : 'bg-red-500/20 text-red-400'
        }`}>
          {trade.direction === 'LONG' ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
          {trade.direction}
        </span>
      </td>
      <td className="py-3 px-4 text-sm text-muted-foreground">{trade.entry_date}</td>
      <td className="py-3 px-4 text-sm">${trade.entry_price.toFixed(2)}</td>
      <td className="py-3 px-4 text-sm">${trade.exit_price.toFixed(2)}</td>
      <td className={`py-3 px-4 text-sm font-medium ${isWin ? 'text-green-400' : 'text-red-400'}`}>
        {isWin ? '+' : ''}{trade.pnl.toFixed(2)}$
      </td>
      <td className={`py-3 px-4 text-sm font-medium ${isWin ? 'text-green-400' : 'text-red-400'}`}>
        {isWin ? '+' : ''}{trade.pnl_percent.toFixed(2)}%
      </td>
      <td className="py-3 px-4 text-xs text-muted-foreground max-w-xs truncate" title={trade.signal_reason}>
        {trade.signal_reason}
      </td>
    </tr>
  );
};

const Backtest = () => {
  const { language } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [strategies, setStrategies] = useState([]);
  const [results, setResults] = useState(null);
  const [favorites, setFavorites] = useState([]);
  
  // Form state
  const [symbol, setSymbol] = useState('BINANCE:BTCUSDT');
  const [period, setPeriod] = useState('6mo');
  const [timeframe, setTimeframe] = useState('1D');
  const [strategy, setStrategy] = useState('combined');
  const [initialCapital, setInitialCapital] = useState('10000');
  const [riskPerTrade, setRiskPerTrade] = useState('2');
  const [leverage, setLeverage] = useState('1');

  useEffect(() => {
    fetchStrategies();
    fetchFavorites();
  }, []);

  const fetchStrategies = async () => {
    try {
      const response = await axios.get(`${API}/backtest/strategies`);
      setStrategies(response.data.strategies);
    } catch (error) {
      console.error('Failed to fetch strategies:', error);
    }
  };

  const fetchFavorites = async () => {
    try {
      const response = await axios.get(`${API}/watchlist/symbols`);
      setFavorites(response.data.symbols || []);
    } catch (error) {
      console.error('Failed to fetch favorites:', error);
    }
  };

  const runBacktest = async () => {
    if (!symbol.trim()) {
      toast.error(language === 'fr' ? 'Veuillez entrer un symbole' : 'Please enter a symbol');
      return;
    }

    setLoading(true);
    setResults(null);

    try {
      const response = await axios.post(`${API}/backtest`, {
        symbol: symbol.trim(),
        period,
        timeframe,
        strategy,
        initial_capital: parseFloat(initialCapital),
        risk_per_trade: parseFloat(riskPerTrade),
        leverage: parseInt(leverage)
      });
      
      setResults(response.data);
      toast.success(language === 'fr' ? 'Backtest terminé !' : 'Backtest completed!');
    } catch (error) {
      console.error('Backtest failed:', error);
      const errorMsg = error.response?.data?.detail || 'Backtest failed';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const strategyInfo = strategies.find(s => s.id === strategy);

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl" data-testid="backtest-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold flex items-center gap-3 mb-2">
          <BarChart3 className="w-8 h-8 text-primary" />
          {language === 'fr' ? 'Backtesting' : 'Backtesting'}
        </h1>
        <p className="text-muted-foreground">
          {language === 'fr' 
            ? 'Testez vos stratégies sur des données historiques avant de trader avec du capital réel'
            : 'Test your strategies on historical data before trading with real capital'}
        </p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Configuration Panel */}
        <div className="lg:col-span-1">
          <Card className="bg-card/50 border-border/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="w-5 h-5 text-primary" />
                {language === 'fr' ? 'Configuration' : 'Configuration'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Symbol Selection */}
              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Symbole' : 'Symbol'}</Label>
                <Select value={symbol} onValueChange={setSymbol}>
                  <SelectTrigger data-testid="symbol-select">
                    <SelectValue placeholder="Sélectionner..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="BINANCE:BTCUSDT">BTC/USDT</SelectItem>
                    <SelectItem value="BINANCE:ETHUSDT">ETH/USDT</SelectItem>
                    <SelectItem value="BINANCE:SOLUSDT">SOL/USDT</SelectItem>
                    <SelectItem value="NASDAQ:AAPL">Apple (AAPL)</SelectItem>
                    <SelectItem value="NASDAQ:NVDA">NVIDIA (NVDA)</SelectItem>
                    <SelectItem value="NASDAQ:TSLA">Tesla (TSLA)</SelectItem>
                    <SelectItem value="FX:EURUSD">EUR/USD</SelectItem>
                    <SelectItem value="TVC:GOLD">Gold</SelectItem>
                    {favorites.map(fav => (
                      <SelectItem key={fav.symbol} value={fav.symbol}>
                        {fav.name || fav.symbol}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input 
                  placeholder="Ou entrez manuellement..."
                  value={symbol}
                  onChange={(e) => setSymbol(e.target.value)}
                  className="mt-2"
                  data-testid="symbol-input"
                />
              </div>

              {/* Strategy Selection */}
              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Stratégie' : 'Strategy'}</Label>
                <Select value={strategy} onValueChange={setStrategy}>
                  <SelectTrigger data-testid="strategy-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(STRATEGIES).map(([key, val]) => (
                      <SelectItem key={key} value={key}>
                        <div className="flex items-center gap-2">
                          <val.icon className={`w-4 h-4 ${val.color}`} />
                          <span>{val.name}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {strategyInfo && (
                  <div className="text-xs text-muted-foreground mt-1 p-2 bg-muted/30 rounded">
                    <Info className="w-3 h-3 inline mr-1" />
                    {strategyInfo.description}
                  </div>
                )}
              </div>

              {/* Period & Timeframe */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{language === 'fr' ? 'Période' : 'Period'}</Label>
                  <Select value={period} onValueChange={setPeriod}>
                    <SelectTrigger data-testid="period-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PERIODS.map(p => (
                        <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{language === 'fr' ? 'Timeframe' : 'Timeframe'}</Label>
                  <Select value={timeframe} onValueChange={setTimeframe}>
                    <SelectTrigger data-testid="timeframe-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TIMEFRAMES.map(tf => (
                        <SelectItem key={tf.value} value={tf.value}>{tf.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Capital & Risk */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{language === 'fr' ? 'Capital ($)' : 'Capital ($)'}</Label>
                  <Input
                    type="number"
                    value={initialCapital}
                    onChange={(e) => setInitialCapital(e.target.value)}
                    min="100"
                    step="100"
                    data-testid="capital-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{language === 'fr' ? 'Risque/Trade (%)' : 'Risk/Trade (%)'}</Label>
                  <Input
                    type="number"
                    value={riskPerTrade}
                    onChange={(e) => setRiskPerTrade(e.target.value)}
                    min="0.5"
                    max="10"
                    step="0.5"
                    data-testid="risk-input"
                  />
                </div>
              </div>

              {/* Leverage */}
              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Levier' : 'Leverage'}</Label>
                <Select value={leverage} onValueChange={setLeverage}>
                  <SelectTrigger data-testid="leverage-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[1, 2, 3, 5, 10, 20, 50].map(l => (
                      <SelectItem key={l} value={l.toString()}>{l}x</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Run Button */}
              <Button 
                onClick={runBacktest} 
                disabled={loading}
                className="w-full mt-4"
                size="lg"
                data-testid="run-backtest-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {language === 'fr' ? 'Analyse en cours...' : 'Analyzing...'}
                  </>
                ) : (
                  <>
                    <Play className="w-4 h-4 mr-2" />
                    {language === 'fr' ? 'Lancer le Backtest' : 'Run Backtest'}
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Results Panel */}
        <div className="lg:col-span-2">
          {!results && !loading && (
            <Card className="bg-card/50 border-border/50 h-full min-h-[400px] flex items-center justify-center">
              <div className="text-center p-8">
                <BarChart3 className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
                <h3 className="text-lg font-medium text-muted-foreground mb-2">
                  {language === 'fr' ? 'Aucun résultat' : 'No Results'}
                </h3>
                <p className="text-sm text-muted-foreground/70">
                  {language === 'fr' 
                    ? 'Configurez vos paramètres et lancez un backtest pour voir les résultats'
                    : 'Configure your parameters and run a backtest to see results'}
                </p>
              </div>
            </Card>
          )}

          {loading && (
            <Card className="bg-card/50 border-border/50 h-full min-h-[400px] flex items-center justify-center">
              <div className="text-center p-8">
                <Loader2 className="w-16 h-16 mx-auto text-primary animate-spin mb-4" />
                <h3 className="text-lg font-medium mb-2">
                  {language === 'fr' ? 'Backtest en cours...' : 'Running Backtest...'}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {language === 'fr' 
                    ? 'Récupération des données historiques et simulation des trades'
                    : 'Fetching historical data and simulating trades'}
                </p>
              </div>
            </Card>
          )}

          {results && (
            <div className="space-y-6">
              {/* Summary Stats */}
              <Card className="bg-card/50 border-border/50">
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <Trophy className="w-5 h-5 text-yellow-400" />
                      {language === 'fr' ? 'Résultats' : 'Results'}: {results.symbol}
                    </span>
                    <span className={`text-2xl font-bold ${results.total_pnl >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {results.total_pnl >= 0 ? '+' : ''}{results.total_pnl.toFixed(2)}$
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <StatCard
                      icon={DollarSign}
                      label={language === 'fr' ? 'Capital Final' : 'Final Capital'}
                      value={`$${results.final_capital.toLocaleString()}`}
                      subValue={`${results.total_pnl_percent >= 0 ? '+' : ''}${results.total_pnl_percent.toFixed(2)}%`}
                      color={results.total_pnl >= 0 ? 'text-green-400' : 'text-red-400'}
                      bgColor={results.total_pnl >= 0 ? 'bg-green-500/10' : 'bg-red-500/10'}
                    />
                    <StatCard
                      icon={Percent}
                      label={language === 'fr' ? 'Taux de Réussite' : 'Win Rate'}
                      value={`${results.win_rate}%`}
                      subValue={`${results.winning_trades}W / ${results.losing_trades}L`}
                      color={results.win_rate >= 50 ? 'text-green-400' : 'text-yellow-400'}
                      bgColor={results.win_rate >= 50 ? 'bg-green-500/10' : 'bg-yellow-500/10'}
                    />
                    <StatCard
                      icon={Activity}
                      label={language === 'fr' ? 'Total Trades' : 'Total Trades'}
                      value={results.total_trades}
                      subValue={`Profit Factor: ${results.profit_factor}`}
                      color="text-blue-400"
                      bgColor="bg-blue-500/10"
                    />
                    <StatCard
                      icon={AlertTriangle}
                      label={language === 'fr' ? 'Max Drawdown' : 'Max Drawdown'}
                      value={`-$${results.max_drawdown.toFixed(2)}`}
                      subValue={`-${results.max_drawdown_percent}%`}
                      color="text-orange-400"
                      bgColor="bg-orange-500/10"
                    />
                  </div>

                  {/* Best/Worst Trades */}
                  <div className="grid grid-cols-2 gap-4 mt-4">
                    <div className="bg-green-500/10 rounded-lg p-4 border border-green-500/20">
                      <div className="flex items-center gap-2 text-green-400 mb-1">
                        <Trophy className="w-4 h-4" />
                        <span className="text-sm">{language === 'fr' ? 'Meilleur Trade' : 'Best Trade'}</span>
                      </div>
                      <div className="text-xl font-bold text-green-400">+${results.best_trade.toFixed(2)}</div>
                      <div className="text-xs text-muted-foreground mt-1">
                        {language === 'fr' ? 'Gain moyen' : 'Avg Win'}: ${results.average_win.toFixed(2)}
                      </div>
                    </div>
                    <div className="bg-red-500/10 rounded-lg p-4 border border-red-500/20">
                      <div className="flex items-center gap-2 text-red-400 mb-1">
                        <Skull className="w-4 h-4" />
                        <span className="text-sm">{language === 'fr' ? 'Pire Trade' : 'Worst Trade'}</span>
                      </div>
                      <div className="text-xl font-bold text-red-400">${results.worst_trade.toFixed(2)}</div>
                      <div className="text-xs text-muted-foreground mt-1">
                        {language === 'fr' ? 'Perte moyenne' : 'Avg Loss'}: ${results.average_loss.toFixed(2)}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Equity Curve */}
              {results.equity_curve && results.equity_curve.length > 0 && (
                <Card className="bg-card/50 border-border/50">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <LineChart className="w-5 h-5 text-primary" />
                      {language === 'fr' ? 'Courbe de Capital' : 'Equity Curve'}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <RechartsLineChart data={results.equity_curve}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                          <XAxis 
                            dataKey="date" 
                            tick={{ fontSize: 10, fill: '#888' }}
                            tickFormatter={(value) => value.split(' ')[0]}
                            interval="preserveStartEnd"
                          />
                          <YAxis 
                            tick={{ fontSize: 10, fill: '#888' }}
                            tickFormatter={(value) => `$${value.toLocaleString()}`}
                          />
                          <Tooltip 
                            contentStyle={{ 
                              backgroundColor: '#1a1a2e', 
                              border: '1px solid #333',
                              borderRadius: '8px'
                            }}
                            formatter={(value) => [`$${value.toLocaleString()}`, 'Capital']}
                          />
                          <ReferenceLine 
                            y={results.initial_capital} 
                            stroke="#666" 
                            strokeDasharray="5 5"
                            label={{ value: 'Initial', position: 'right', fill: '#666', fontSize: 10 }}
                          />
                          <Line 
                            type="monotone" 
                            dataKey="equity" 
                            stroke={results.total_pnl >= 0 ? '#22c55e' : '#ef4444'}
                            strokeWidth={2}
                            dot={false}
                          />
                        </RechartsLineChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Trades Table */}
              <Card className="bg-card/50 border-border/50">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <History className="w-5 h-5 text-primary" />
                    {language === 'fr' ? 'Historique des Trades' : 'Trade History'}
                    <span className="text-sm text-muted-foreground font-normal">
                      ({results.trades.length} trades)
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-border text-left text-sm text-muted-foreground">
                          <th className="py-3 px-4">#</th>
                          <th className="py-3 px-4">{language === 'fr' ? 'Direction' : 'Direction'}</th>
                          <th className="py-3 px-4">{language === 'fr' ? 'Entrée' : 'Entry'}</th>
                          <th className="py-3 px-4">{language === 'fr' ? 'Prix Entrée' : 'Entry Price'}</th>
                          <th className="py-3 px-4">{language === 'fr' ? 'Prix Sortie' : 'Exit Price'}</th>
                          <th className="py-3 px-4">{language === 'fr' ? 'PNL ($)' : 'PNL ($)'}</th>
                          <th className="py-3 px-4">{language === 'fr' ? 'PNL (%)' : 'PNL (%)'}</th>
                          <th className="py-3 px-4">{language === 'fr' ? 'Signal' : 'Signal'}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {results.trades.slice(0, 20).map((trade, index) => (
                          <TradeRow key={index} trade={trade} index={index} />
                        ))}
                      </tbody>
                    </table>
                    {results.trades.length > 20 && (
                      <div className="text-center py-4 text-sm text-muted-foreground">
                        {language === 'fr' 
                          ? `... et ${results.trades.length - 20} autres trades`
                          : `... and ${results.trades.length - 20} more trades`}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Backtest;
